import React, { useEffect, useState } from 'react';
import { getPrestamos, createPrestamo, getClientes, getContratoPdfUrl, getPagarePdfUrl, renovarPrestamo, enviarNotificacion } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Wallet, Calendar, FileText, Eye, Download, FileSignature, RefreshCw, MessageCircle, Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

const estadoColors = {
    activo: 'bg-primary/10 text-primary border-primary/20',
    en_mora: 'bg-destructive/10 text-destructive border-destructive/20',
    cancelado: 'bg-accent/10 text-accent border-accent/20',
    renovado: 'bg-warning/10 text-warning border-warning/20',
};

const estadoLabels = {
    activo: 'Activo',
    en_mora: 'En Mora',
    cancelado: 'Cancelado',
    renovado: 'Renovado',
};

const frecuenciaLabels = {
    diario: 'Diario',
    semanal: 'Semanal',
    quincenal: 'Quincenal',
    mensual: 'Mensual',
};

export default function PrestamosPage() {
    const { hasRole } = useAuth();
    const [prestamos, setPrestamos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [renovarDialogOpen, setRenovarDialogOpen] = useState(false);
    const [selectedPrestamo, setSelectedPrestamo] = useState(null);
    const [renovarForm, setRenovarForm] = useState({
        monto_adicional: '',
        nueva_tasa: '',
        nuevo_plazo: '',
    });
    const [form, setForm] = useState({
        cliente_id: '',
        monto: '',
        tasa_interes: '10',
        plazo: '',
        frecuencia: 'mensual',
        tipo_calculo: 'simple',
        fecha_inicio: new Date().toISOString().split('T')[0],
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [prestamosRes, clientesRes] = await Promise.all([
                getPrestamos(),
                getClientes()
            ]);
            setPrestamos(prestamosRes.data);
            setClientes(clientesRes.data);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...form,
                monto: parseFloat(form.monto),
                tasa_interes: parseFloat(form.tasa_interes),
                plazo: parseInt(form.plazo),
            };
            await createPrestamo(data);
            toast.success('Préstamo creado exitosamente');
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear préstamo');
        }
    };

    const resetForm = () => {
        setForm({
            cliente_id: '',
            monto: '',
            tasa_interes: '10',
            plazo: '',
            frecuencia: 'mensual',
            tipo_calculo: 'simple',
            fecha_inicio: new Date().toISOString().split('T')[0],
        });
    };

    const viewDetail = (prestamo) => {
        setSelectedPrestamo(prestamo);
        setDetailDialogOpen(true);
    };

    const downloadContrato = (prestamoId) => {
        const token = localStorage.getItem('token');
        const url = getContratoPdfUrl(prestamoId);
        window.open(`${url}?token=${token}`, '_blank');
    };

    const downloadPagare = (prestamoId) => {
        const token = localStorage.getItem('token');
        const url = getPagarePdfUrl(prestamoId);
        window.open(`${url}?token=${token}`, '_blank');
    };

    const printDocument = (type, id) => {
        const token = localStorage.getItem('token');
        const url = type === 'contrato' ? getContratoPdfUrl(id) : getPagarePdfUrl(id);
        
        // Abrir PDF en nueva ventana para imprimir
        fetch(`${url}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                };
            }
        })
        .catch(error => {
            console.error('Error al imprimir:', error);
            toast.error('Error al preparar el documento para impresión');
        });
    };

    const openRenovarDialog = (prestamo) => {
        setSelectedPrestamo(prestamo);
        setRenovarForm({
            monto_adicional: '',
            nueva_tasa: prestamo.tasa_interes.toString(),
            nuevo_plazo: prestamo.plazo.toString(),
        });
        setRenovarDialogOpen(true);
    };

    const handleRenovar = async () => {
        if (!selectedPrestamo) return;
        try {
            const data = {
                prestamo_id: selectedPrestamo.id,
                monto_adicional: parseFloat(renovarForm.monto_adicional) || 0,
                nueva_tasa: renovarForm.nueva_tasa ? parseFloat(renovarForm.nueva_tasa) : null,
                nuevo_plazo: renovarForm.nuevo_plazo ? parseInt(renovarForm.nuevo_plazo) : null,
            };
            const { data: result } = await renovarPrestamo(selectedPrestamo.id, data);
            toast.success(`Préstamo renovado. Entregar: ${formatCurrency(result.dinero_a_entregar)}`);
            setRenovarDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al renovar préstamo');
        }
    };

    const sendWhatsAppReminder = async (prestamo) => {
        try {
            await enviarNotificacion({
                prestamo_id: prestamo.id,
                tipo: 'recordatorio'
            });
            toast.success('Recordatorio enviado por WhatsApp');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al enviar recordatorio');
        }
    };

    // Calculate preview
    const calculatePreview = () => {
        if (!form.monto || !form.plazo || !form.tasa_interes) return null;
        
        const monto = parseFloat(form.monto);
        const tasa = parseFloat(form.tasa_interes) / 100;
        const plazo = parseInt(form.plazo);
        
        if (form.tipo_calculo === 'simple') {
            const interes = monto * tasa * plazo;
            const total = monto + interes;
            const cuota = total / plazo;
            return { total, cuota, interes };
        } else {
            // Sistema francés
            if (tasa === 0) {
                return { total: monto, cuota: monto / plazo, interes: 0 };
            }
            const cuota = monto * (tasa * Math.pow(1 + tasa, plazo)) / (Math.pow(1 + tasa, plazo) - 1);
            const total = cuota * plazo;
            const interes = total - monto;
            return { total, cuota, interes };
        }
    };

    const preview = calculatePreview();

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="prestamos-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Préstamos</h1>
                    <p className="text-muted-foreground mt-1">{prestamos.length} préstamos registrados</p>
                </div>
                {hasRole(['admin', 'cajero', 'supervisor']) && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-prestamo-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nuevo Préstamo
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading">Crear Préstamo</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="cliente">Cliente</Label>
                                    <Select
                                        value={form.cliente_id}
                                        onValueChange={(value) => setForm({ ...form, cliente_id: value })}
                                    >
                                        <SelectTrigger data-testid="prestamo-cliente-select">
                                            <SelectValue placeholder="Seleccionar cliente" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {clientes.filter(c => c.estado === 'activo').map((cliente) => (
                                                <SelectItem key={cliente.id} value={cliente.id}>
                                                    {cliente.nombre} - {cliente.cedula}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="monto">Monto</Label>
                                        <Input
                                            id="monto"
                                            type="number"
                                            value={form.monto}
                                            onChange={(e) => setForm({ ...form, monto: e.target.value })}
                                            required
                                            min="100"
                                            data-testid="prestamo-monto-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="tasa">Tasa de Interés (%)</Label>
                                        <Input
                                            id="tasa"
                                            type="number"
                                            value={form.tasa_interes}
                                            onChange={(e) => setForm({ ...form, tasa_interes: e.target.value })}
                                            required
                                            min="0"
                                            step="0.1"
                                            data-testid="prestamo-tasa-input"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="plazo">Plazo (períodos)</Label>
                                        <Input
                                            id="plazo"
                                            type="number"
                                            value={form.plazo}
                                            onChange={(e) => setForm({ ...form, plazo: e.target.value })}
                                            required
                                            min="1"
                                            data-testid="prestamo-plazo-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="frecuencia">Frecuencia</Label>
                                        <Select
                                            value={form.frecuencia}
                                            onValueChange={(value) => setForm({ ...form, frecuencia: value })}
                                        >
                                            <SelectTrigger data-testid="prestamo-frecuencia-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="diario">Diario</SelectItem>
                                                <SelectItem value="semanal">Semanal</SelectItem>
                                                <SelectItem value="quincenal">Quincenal</SelectItem>
                                                <SelectItem value="mensual">Mensual</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="tipo">Tipo de Cálculo</Label>
                                        <Select
                                            value={form.tipo_calculo}
                                            onValueChange={(value) => setForm({ ...form, tipo_calculo: value })}
                                        >
                                            <SelectTrigger data-testid="prestamo-tipo-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="simple">Interés Simple</SelectItem>
                                                <SelectItem value="frances">Sistema Francés</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="fecha">Fecha de Inicio</Label>
                                        <Input
                                            id="fecha"
                                            type="date"
                                            value={form.fecha_inicio}
                                            onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                                            required
                                            data-testid="prestamo-fecha-input"
                                        />
                                    </div>
                                </div>

                                {/* Preview */}
                                {preview && (
                                    <Card className="bg-muted/50">
                                        <CardContent className="p-4">
                                            <p className="text-sm font-medium mb-2">Vista Previa</p>
                                            <div className="grid grid-cols-3 gap-2 text-sm">
                                                <div>
                                                    <p className="text-muted-foreground">Total a Pagar</p>
                                                    <p className="font-mono font-bold">{formatCurrency(preview.total)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Cuota</p>
                                                    <p className="font-mono font-bold">{formatCurrency(preview.cuota)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Interés Total</p>
                                                    <p className="font-mono font-bold">{formatCurrency(preview.interes)}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={!form.cliente_id} data-testid="prestamo-submit-btn">
                                        Crear Préstamo
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Cliente</TableHead>
                                    <TableHead>Monto</TableHead>
                                    <TableHead className="hidden md:table-cell">Frecuencia</TableHead>
                                    <TableHead className="hidden lg:table-cell">Saldo</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="w-20"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {prestamos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-12">
                                            <Wallet className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">No hay préstamos registrados</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    prestamos.map((prestamo) => (
                                        <TableRow key={prestamo.id} className="animate-fadeIn">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{prestamo.cliente_nombre}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {prestamo.fecha_inicio}
                                                    </p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-mono font-bold">{formatCurrency(prestamo.monto)}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {prestamo.tasa_interes}% - {prestamo.tipo_calculo}
                                                </p>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <Badge variant="secondary">
                                                    {frecuenciaLabels[prestamo.frecuencia]}
                                                </Badge>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {prestamo.plazo} cuotas
                                                </p>
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell">
                                                <p className="font-mono">{formatCurrency(prestamo.saldo_pendiente)}</p>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={estadoColors[prestamo.estado]}>
                                                    {estadoLabels[prestamo.estado]}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1 flex-wrap">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => viewDetail(prestamo)}
                                                        data-testid={`view-prestamo-${prestamo.id}`}
                                                        title="Ver detalle"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => downloadContrato(prestamo.id)}
                                                        data-testid={`download-contrato-${prestamo.id}`}
                                                        title="Descargar contrato"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => downloadPagare(prestamo.id)}
                                                        data-testid={`download-pagare-${prestamo.id}`}
                                                        title="Descargar pagaré"
                                                    >
                                                        <FileSignature className="w-4 h-4" />
                                                    </Button>
                                                    {prestamo.estado !== 'cancelado' && hasRole(['admin', 'supervisor']) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => openRenovarDialog(prestamo)}
                                                            data-testid={`renovar-prestamo-${prestamo.id}`}
                                                            title="Renovar préstamo"
                                                        >
                                                            <RefreshCw className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {prestamo.estado !== 'cancelado' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => sendWhatsAppReminder(prestamo)}
                                                            data-testid={`whatsapp-prestamo-${prestamo.id}`}
                                                            title="Enviar recordatorio WhatsApp"
                                                            className="text-green-600 hover:text-green-700"
                                                        >
                                                            <MessageCircle className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Detalle del Préstamo</DialogTitle>
                    </DialogHeader>
                    {selectedPrestamo && (
                        <div className="space-y-6">
                            {/* Botones de descarga e impresión */}
                            <div className="flex gap-2 justify-end">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadContrato(selectedPrestamo.id)}
                                    data-testid="detail-download-contrato"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Contrato
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadPagare(selectedPrestamo.id)}
                                    data-testid="detail-download-pagare"
                                >
                                    <FileSignature className="w-4 h-4 mr-2" />
                                    Pagaré
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => printDocument('contrato', selectedPrestamo.id)}
                                    data-testid="detail-print-contrato"
                                >
                                    <Printer className="w-4 h-4 mr-2" />
                                    Imprimir Contrato
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => printDocument('pagare', selectedPrestamo.id)}
                                    data-testid="detail-print-pagare"
                                >
                                    <Printer className="w-4 h-4 mr-2" />
                                    Imprimir Pagaré
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Cliente</p>
                                    <p className="font-medium">{selectedPrestamo.cliente_nombre}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Monto</p>
                                    <p className="font-mono font-bold">{formatCurrency(selectedPrestamo.monto)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total a Pagar</p>
                                    <p className="font-mono font-bold">{formatCurrency(selectedPrestamo.total_pagar)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Saldo Pendiente</p>
                                    <p className="font-mono font-bold text-primary">{formatCurrency(selectedPrestamo.saldo_pendiente)}</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-heading font-semibold mb-3">Cronograma de Pagos</h4>
                                <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>#</TableHead>
                                                <TableHead>Fecha</TableHead>
                                                <TableHead>Capital</TableHead>
                                                <TableHead>Interés</TableHead>
                                                <TableHead>Total</TableHead>
                                                <TableHead>Estado</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {selectedPrestamo.cuotas.map((cuota) => (
                                                <TableRow key={cuota.numero}>
                                                    <TableCell className="font-mono">{cuota.numero}</TableCell>
                                                    <TableCell>{cuota.fecha_vencimiento}</TableCell>
                                                    <TableCell className="font-mono">{formatCurrency(cuota.capital)}</TableCell>
                                                    <TableCell className="font-mono">{formatCurrency(cuota.interes)}</TableCell>
                                                    <TableCell className="font-mono font-bold">{formatCurrency(cuota.total)}</TableCell>
                                                    <TableCell>
                                                        <Badge 
                                                            variant="outline" 
                                                            className={
                                                                cuota.estado === 'pagada' 
                                                                    ? 'bg-accent/10 text-accent' 
                                                                    : cuota.mora > 0 
                                                                        ? 'bg-destructive/10 text-destructive'
                                                                        : ''
                                                            }
                                                        >
                                                            {cuota.estado === 'pagada' ? 'Pagada' : 
                                                             cuota.estado === 'parcialmente_pagada' ? 'Parcial' : 
                                                             cuota.mora > 0 ? `Mora: ${formatCurrency(cuota.mora)}` : 'Pendiente'}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Renovar Dialog */}
            <Dialog open={renovarDialogOpen} onOpenChange={setRenovarDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Renovar Préstamo</DialogTitle>
                    </DialogHeader>
                    {selectedPrestamo && (
                        <div className="space-y-4">
                            <Card className="bg-muted/50">
                                <CardContent className="p-4">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-muted-foreground">Cliente</p>
                                            <p className="font-medium">{selectedPrestamo.cliente_nombre}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Saldo Actual</p>
                                            <p className="font-mono font-bold text-primary">
                                                {formatCurrency(selectedPrestamo.saldo_pendiente)}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="space-y-2">
                                <Label htmlFor="monto_adicional">Monto Adicional a Entregar</Label>
                                <Input
                                    id="monto_adicional"
                                    type="number"
                                    value={renovarForm.monto_adicional}
                                    onChange={(e) => setRenovarForm({ ...renovarForm, monto_adicional: e.target.value })}
                                    placeholder="0.00"
                                    min="0"
                                    data-testid="renovar-monto-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Nuevo monto total: {formatCurrency(selectedPrestamo.saldo_pendiente + (parseFloat(renovarForm.monto_adicional) || 0))}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nueva_tasa">Nueva Tasa (%)</Label>
                                    <Input
                                        id="nueva_tasa"
                                        type="number"
                                        value={renovarForm.nueva_tasa}
                                        onChange={(e) => setRenovarForm({ ...renovarForm, nueva_tasa: e.target.value })}
                                        step="0.1"
                                        min="0"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nuevo_plazo">Nuevo Plazo</Label>
                                    <Input
                                        id="nuevo_plazo"
                                        type="number"
                                        value={renovarForm.nuevo_plazo}
                                        onChange={(e) => setRenovarForm({ ...renovarForm, nuevo_plazo: e.target.value })}
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setRenovarDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button onClick={handleRenovar} data-testid="renovar-submit-btn">
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Renovar Préstamo
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
